import { useState } from 'react';
import { unifiedApiService } from '../../services/unified';
import { ResumeProcessingStatus } from './ResumeProcessingStatus';
import type { Resume } from '../../types/resume';

// Helper functions
const formatFileSize = (bytes: number | undefined | null): string => {
  if (!bytes || bytes === 0) return '';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};

const getFileExtension = (filename: string): string => {
  return filename.split('.').pop()?.toLowerCase() || '';
};

interface ResumeVersionHistoryProps {
  resumes: Resume[];
  onResumeUpdate: (resume: Resume) => void;
  onResumeDelete: (resumeId: string) => void;
  className?: string;
}

export function ResumeVersionHistory({ 
  resumes, 
  onResumeUpdate, 
  onResumeDelete,
  className = '' 
}: ResumeVersionHistoryProps) {
  const [downloadingIds, setDownloadingIds] = useState<Set<string>>(new Set());
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<Resume | null>(null);

  const handleDownload = async (resume: Resume) => {
    setDownloadingIds(prev => new Set(prev).add(resume.id));
    setError(null);

    try {
      const blob = await unifiedApiService.resume.downloadResume(resume.id);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = resume.originalFilename || resume.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      const error = err as Error;
      setError(error.message || 'Failed to download resume');
    } finally {
      setDownloadingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(resume.id);
        return newSet;
      });
    }
  };

  const handleDelete = async (resume: Resume) => {
    setDeletingIds(prev => new Set(prev).add(resume.id));
    setError(null);

    try {
      await unifiedApiService.resume.deleteResume(resume.id);
      onResumeDelete(resume.id);
      setDeleteConfirm(null);
    } catch (err) {
      const error = err as Error;
      // Show more user-friendly error message
      const errorMessage = error.message || 'Failed to delete resume';
      setError(errorMessage);
      setDeleteConfirm(null);
    } finally {
      setDeletingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(resume.id);
        return newSet;
      });
    }
  };

  const handleRetryProcessing = (resumeId: string) => {
    // Find the resume and update its status to processing
    const resume = resumes.find(r => r.id === resumeId);
    if (resume) {
      onResumeUpdate({ ...resume, status: 'processing' });
    }
  };

  const formatDate = (dateString: string | undefined | null) => {
    if (!dateString) return 'Unknown date';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Unknown date';
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return 'Unknown date';
    }
  };

  const getFileIcon = (filename: string) => {
    const extension = getFileExtension(filename);
    
    if (extension === 'pdf') {
      return (
        <svg className="h-8 w-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      );
    } else {
      return (
        <svg className="h-8 w-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      );
    }
  };

  if (resumes.length === 0) {
    return (
      <div className={`text-center py-8 ${className}`}>
        <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        <h3 className="mt-2 text-sm font-medium text-gray-900">No resumes uploaded</h3>
        <p className="mt-1 text-sm text-gray-500">Get started by uploading your first resume.</p>
      </div>
    );
  }

  return (
    <div className={className}>
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-red-800">Error</p>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-4">
        {resumes.map((resume) => (
          <div
            key={resume.id}
            className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow duration-200"
          >
            <div className="flex items-start space-x-4">
              {/* File Icon */}
              <div className="flex-shrink-0">
                {getFileIcon(resume.filename)}
              </div>

              {/* Resume Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-gray-900 truncate">
                      {resume.originalFilename || resume.filename}
                    </h3>
                    <div className="mt-1 flex items-center space-x-4 text-xs text-gray-500">
                      {formatFileSize(resume.fileSize) && (
                        <>
                          <span>{formatFileSize(resume.fileSize)}</span>
                          <span>•</span>
                        </>
                      )}
                      <span>Uploaded {formatDate(resume.submittedAt)}</span>
                      {resume.processedAt && (
                        <>
                          <span>•</span>
                          <span>Processed {formatDate(resume.processedAt)}</span>
                        </>
                      )}
                    </div>
                    {resume.score && (
                      <div className="mt-1">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                          Score: {resume.score}/10
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center space-x-2 ml-4">
                    <button
                      onClick={() => handleDownload(resume)}
                      disabled={downloadingIds.has(resume.id)}
                      className="inline-flex items-center p-1.5 border border-gray-300 rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Download resume"
                    >
                      {downloadingIds.has(resume.id) ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b border-gray-700"></div>
                      ) : (
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      )}
                    </button>

                    <button
                      onClick={() => setDeleteConfirm(resume)}
                      disabled={deletingIds.has(resume.id)}
                      className="inline-flex items-center p-1.5 border border-gray-300 rounded text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Delete resume"
                    >
                      {deletingIds.has(resume.id) ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b border-red-700"></div>
                      ) : (
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>

                {/* Processing Status */}
                <div className="mt-3">
                  <ResumeProcessingStatus
                    resume={resume}
                    onStatusUpdate={onResumeUpdate}
                    onRetry={handleRetryProcessing}
                  />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full shadow-xl">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-medium text-gray-900 mb-2">Delete Resume</h3>
                <p className="text-sm text-gray-600 mb-1">
                  Are you sure you want to delete <span className="font-medium">"{deleteConfirm.originalFilename || deleteConfirm.filename}"</span>?
                </p>
                <p className="text-sm text-gray-600 mb-4">
                  This action cannot be undone.
                </p>
                <div className="flex space-x-3 justify-end">
                  <button
                    onClick={() => setDeleteConfirm(null)}
                    disabled={deletingIds.has(deleteConfirm.id)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleDelete(deleteConfirm)}
                    disabled={deletingIds.has(deleteConfirm.id)}
                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md disabled:opacity-50"
                  >
                    {deletingIds.has(deleteConfirm.id) ? 'Deleting...' : 'Delete Resume'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}